import moment from "moment-timezone";
import SocketService from "./SocketService";
import ISystemStatusMessageUI, { ISystemStatusRemote } from "../models/ISystemStatusMessageUI";

/**
 * Reads the Orderwerks system status message from the public S3 bucket.
 * Mirrors orderwerks-2021-site/components/MaintenanceModeService.tsx so the
 * dashboard widget and the marketing site login page show the same message.
 */

const STATUS_BUCKET_URL = "https://orderwerks-status.s3.us-east-2.amazonaws.com";
const FETCH_TIMEOUT_MS = 5000;
const DEFAULT_ICON = "fas fa-info";
const DEFAULT_CLASS_NAME = "text-yellow-500";
const DEFAULT_ACCENT_COLOR = "#eab308";

const getStatusUrl = (): string => {
    switch (SocketService.getEnvironment()) {
        case "prod":
        case "test":
            return `${STATUS_BUCKET_URL}/status.json`;
        case "development":
        case "docker":
        default:
            return `${STATUS_BUCKET_URL}/dev-status.json`;
    }
};

/**
 * The status file carries a Tailwind text class for the marketing site. This
 * app uses Bootstrap, so map the colour family to a hex accent instead.
 */
const getAccentColor = (className?: string): string => {
    const match = /text-([a-z]+)-\d+/.exec(className || "");
    const family = match ? match[1] : "";

    switch (family) {
        case "yellow":
        case "amber":
            return "#eab308";
        case "orange":
            return "#f97316";
        case "red":
        case "rose":
            return "#ef4444";
        case "green":
        case "emerald":
            return "#22c55e";
        case "blue":
        case "sky":
            return "#3b82f6";
        case "gray":
        case "slate":
            return "#6b7280";
        default:
            return DEFAULT_ACCENT_COLOR;
    }
};

const parseTime = (value?: string): moment.Moment => {
    // moment(undefined) is "now", which would silently pass validity checks.
    return value ? moment(value) : moment.invalid();
};

const formatTimeframe = (start?: string, end?: string, now: moment.Moment = moment()): string | undefined => {
    const s = parseTime(start);
    const e = parseTime(end);

    if (!s.isValid() || !e.isValid() || now.isAfter(e)) {
        return undefined;
    }

    if (s.isSame(e, "day")) {
        return `${s.format("MMM D, YYYY, h:mm A")} – ${e.format("h:mm A")}`;
    }

    return `${s.format("MMM D, YYYY, h:mm A")} – ${e.format("MMM D, YYYY, h:mm A")}`;
};

const formatEndTime = (end?: string): string | undefined => {
    const e = parseTime(end);

    return e.isValid() ? e.format("h:mm A, MMM D YYYY") : undefined;
};

/**
 * Pure decision logic over the raw status file. Returns null when there is
 * nothing to show.
 */
const buildSystemStatusMessage = (data: ISystemStatusRemote, now: moment.Moment = moment()): ISystemStatusMessageUI | null => {
    const message = (data.message || "").trim();
    const icon = data.icon || DEFAULT_ICON;
    const accentColor = getAccentColor(data.className || DEFAULT_CLASS_NAME);

    if (data.maintenance_mode) {
        const start = parseTime(data.start_time);
        const end = parseTime(data.expected_end_time);

        if (start.isValid() && end.isValid() && now.isBetween(start, end)) {
            // Maintenance is underway. The widget supplies a default message
            // when the file does not carry one.
            return {
                kind: "maintenance_in_progress",
                message,
                icon,
                accentColor,
                endTime: formatEndTime(data.expected_end_time),
            };
        }

        if (start.isValid() && now.isBefore(start)) {
            const timeframe = formatTimeframe(data.start_time, data.expected_end_time, now);

            if (!message && !timeframe) {
                return null;
            }

            return { kind: "maintenance_scheduled", message, icon, accentColor, timeframe };
        }

        // Maintenance window has passed (or has no usable start time)
        return null;
    }

    if (!data.show_message) {
        return null;
    }

    const timeframe = formatTimeframe(data.start_time, data.expected_end_time, now);

    if (!message && !timeframe) {
        return null;
    }

    return { kind: "announcement", message, icon, accentColor, timeframe };
};

/**
 * Fetches and normalises the current status. Fails open: any network error,
 * timeout, non-2xx response or malformed body resolves to null.
 */
const fetchSystemStatus = async (): Promise<ISystemStatusMessageUI | null> => {
    try {
        const init: RequestInit = {
            method: "GET",
            // S3 serves this file without Cache-Control, so browsers apply
            // heuristic caching and can hold a stale copy for hours.
            cache: "no-store",
        };

        // Don't let a hanging status fetch keep the widget in a loading state
        if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
            init.signal = AbortSignal.timeout(FETCH_TIMEOUT_MS);
        }

        const resp = await fetch(getStatusUrl(), init);

        if (!resp.ok) {
            return null;
        }

        const data: ISystemStatusRemote = await resp.json();

        return buildSystemStatusMessage(data);
    } catch (err) {
        console.warn("SystemStatusService: unable to load system status", err);

        return null;
    }
};

const SystemStatusService = {
    getStatusUrl,
    getAccentColor,
    formatTimeframe,
    formatEndTime,
    buildSystemStatusMessage,
    fetchSystemStatus,
};

export default SystemStatusService;
